// ═══════════════════════════════════════════════════════════════════════════
//  RETURNING A BLANK PAPER — the act, its warning, and every word of both.
//
//  Registrar ruling, 2026-08-27: **a member MAY deliberately return a blank
//  paper, and the surface must WARN THEM BEFORE ACCEPTING IT.** The capability
//  was already live — an entitled member submitting `{ marks: [] }` gets a 201
//  and one `blank` row per entitled item, pinned end to end at
//  `packages/cca-decides-worker/test-integration/cast-path.test.ts:713` — so
//  what is built here is the CONTROL, not the capability.
//
//  ── IT IS A SEPARATE ACT, NOT A ROW, AND THAT DOES NOT BREAK THE No CONFIDENCE
//     RULE — IT IS THE SAME RULE APPLIED ───────────────────────────────────
//  No Confidence is a row like any other (SO 15.7) because it is an ANSWER to
//  the question the item asks: it competes in the count, and the places it takes
//  go unfilled to Standing Committee (SO 15.12). A blank is not an answer to
//  that question. It is a fact about the PAPER — "handed back with nothing
//  marked on it" — and the wire says so in its own shape: `{ marks: [] }` blanks
//  every entitled item on the ballot at once, so the act is paper-level while a
//  row is item-level. A row would model it at the wrong level, and would be
//  wrong on the first paper that carries two items.
//
//  There is a second reason, and it is about the reader rather than the model. A
//  blank row would land directly beneath "Abstain", and to a member standing in
//  a hall the two read as near-synonyms — the difference (an abstention is a
//  recorded decision; a blank is a paper returned unmarked) is constitutional,
//  not visible. Two adjacent rows a member cannot tell apart, at the moment of
//  an irreversible act, produce a WRONG record rather than merely a blank one.
//
//  ── THE MIS-TAP ASYMMETRY DECIDES HOW THE ACT IS DRESSED, NOT WHETHER IT
//     EXISTS ────────────────────────────────────────────────────────────────
//  No Confidence carries no colour, no icon and no warning because a mis-tap on
//  it records the choice the member touched — a lawful one — and a red row would
//  tell them that choosing it is an error. A blank is the ONE outcome that is
//  also what a mis-tap PRODUCES: an unmarked paper submitted. That is why this
//  act, alone on the paper, asks a second time, and why No Confidence still does
//  not. Three things keep the ask honest rather than a nag:
//    · the trigger sends NOTHING — it opens this screen and no more;
//    · this screen has no pinned action bar, so its confirming button never
//      appears under a thumb that has just pressed "Submit my vote";
//    · the way out is listed FIRST, and cancelling costs the member nothing.
//
//  ── THE WARNING CANNOT LEAK A CHOICE, AND THE REASON IS THE SHAPE ──────────
//  A confirmation dialogue is the highest-exposure moment in the flow — a phone
//  held loosely while the person looks up, a neighbour six inches away. Two
//  properties, both structural rather than remembered:
//
//   1. **{@link BlankConfirm} REPLACES THE PAPER; it does not overlay it.** A
//      modal would leave the marked row and its 3px stripe on screen around the
//      dialogue, which is the exact disclosure CAST-PATH.md:213 forbids, at the
//      exact moment it forbids it. `cast/CastPage.tsx` holds this as a separate
//      arm of its `Screen` union, so the rows are UNMOUNTED while this is up.
//   2. **Its props are `{ act, submitting, onConfirm, onCancel }` and there is
//      no channel through which a mark could arrive.** No `marked`, no `row`,
//      no `label`, no `children`. The same shape argument as ../CastStatus: the
//      sentence is chosen here, from fixed text, and a future caller cannot pass
//      content in even by trying. So the warning is BYTE-IDENTICAL whether the
//      member has marked a row or not.
//
//  That second property is also why the trigger is always present and never
//  conditional on whether anything is marked: a control that appeared only on an
//  unmarked paper would broadcast, to anyone watching the screen, that this
//  member has marked nothing.
//
//  ── ALL THE WORDS OF THIS ACT LIVE IN THIS FILE ────────────────────────────
//  The note on the paper and the warning on the screen are one act said twice,
//  and splitting them across two files is how the second one stops matching the
//  first. What they say, and why each sentence is there:
//    · WHAT A BLANK PAPER IS — a paper handed back with nothing marked, recorded
//      as blank, and neither a vote for anyone nor an abstention. A member who
//      does not know that cannot consent to it.
//    · THAT IT IS PERMANENT — `mark` is append-only and its DELETE is fenced, so
//      a blank cast cannot be undone by the member, the Synod Admin or the
//      registrar, and a second submit is refused by `participation`'s PRIMARY
//      KEY. This is the fact the whole warning exists to carry, so it is stated
//      as what HAPPENS and not as "are you sure?".
//    · WHAT EACH BUTTON DOES, INCLUDING TO A MARK ALREADY MADE. Going back
//      leaves the paper exactly as it was; returning it blank sends it, and
//      anything marked on it is not sent. A member who marked a row and then
//      reached for this act would otherwise lose that mark with no notice — and
//      the sentence is CONDITIONAL, so it is true, identical and equally
//      uninformative whether or not anything is marked. Note the form it avoids:
//      never "your marks are still here", which is a statement about what is on
//      the paper and therefore a channel.
// ═══════════════════════════════════════════════════════════════════════════

import { useRef, type JSX } from 'react';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import { CastStatus, type CastAct } from '../CastStatus';

/**
 * THE DISCRETE CONTROL ON THE PAPER — it sends nothing.
 *
 * A ghost button below the vote and below the items this House may not decide,
 * separated by the paper's own rule, so it reads as another part of the paper
 * rather than as a second submit. It is NOT in the pinned action bar: that bar
 * holds the one primary act, and a second button in it would double the
 * obstruction over the scroll port that `.vote-row`'s `scroll-margin-block-end`
 * reservation is measured against (styles/ballot.css, WCAG 2.2 SC 2.4.11).
 *
 * `type="button"` is load-bearing and not habit: this control sits INSIDE the
 * paper's `<form>`, and a button with no explicit type in a form is a SUBMIT
 * button — so the default would cast the member's marked paper instead of
 * asking them about a blank one.
 */
export function ReturnBlankAction({
  onAsk,
  disabled,
}: {
  onAsk: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="ballot-blank">
      <p className="ballot-note">
        You may return this paper blank instead of marking it. A blank paper is recorded as blank:
        it is not a vote for anyone, and it is not an abstention.
      </p>
      <button
        type="button"
        className="btn btn-ghost btn-block ballot-blank-btn"
        disabled={disabled}
        onClick={onAsk}
      >
        Return this paper blank
      </button>
    </div>
  );
}

interface BlankConfirmProps {
  /** The status line's token. A closed literal union — see ../CastStatus. */
  act: CastAct;
  /** True only while the request is in flight. Locks both buttons, so a second
   *  press cannot arrive behind the first. */
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * THE WARNING, AND THE ONLY PLACE A BLANK PAPER CAN BE SENT FROM.
 *
 * It replaces the paper (see the header), so there is nothing else on the screen
 * — no rows, no marks, no stripe — and focus moves to the heading, which is what
 * announces the change. That is the same construction as `Recorded` in
 * cast/CastPage.tsx and for the same reason: a live region mounted with its
 * first message is not announced by several screen readers, while a focused
 * heading is. The live region below IS mounted empty with this view and only
 * fills later, which is the arrangement that does get announced.
 *
 * THE BUTTONS ARE IN THE FLOW, NOT PINNED, AND CANCEL IS FIRST. A member must
 * read past the warning to reach either, and the destructive one is not where
 * the submit button was on the screen this replaced.
 */
export function BlankConfirm({
  act,
  submitting,
  onConfirm,
  onCancel,
}: BlankConfirmProps): JSX.Element {
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  return (
    <section className="ballot ballot-blank-confirm">
      <h1 className="ballot-title" tabIndex={-1} ref={heading}>
        Return your paper blank?
      </h1>
      <p className="ballot-recorded-text">
        A blank paper is a paper handed back with nothing marked on it. It is recorded as blank on
        every item on this ballot that is yours to mark. It is not a vote for anyone, and it is not
        an abstention.
      </p>
      <p className="ballot-recorded-text">
        <strong>This cannot be undone.</strong> A returned paper is permanent. It cannot be changed,
        replaced or withdrawn afterwards — not by you, not by the Synod Admin, not by the registrar
        — and you will not be able to vote in this ballot again.
      </p>
      <p className="ballot-recorded-text">
        Nothing has been sent yet. Going back leaves your paper exactly as you left it; returning it
        blank sends it, and anything marked on it is not sent.
      </p>

      {/* ABOVE the buttons, for the same reason it sits above the action bar on
          the paper: the failure copy states the fact before any button is
          offered. It is this screen's only live region, and it is the same
          component the paper uses — a closed union of act tokens, incapable of
          carrying a mark. */}
      <CastStatus act={act} />

      <div className="ballot-blank-actions">
        <button
          type="button"
          className="btn btn-ghost btn-block ballot-blank-btn"
          disabled={submitting}
          onClick={onCancel}
        >
          Go back to my paper
        </button>
        <button
          type="button"
          className="btn btn-primary btn-block ballot-blank-btn"
          disabled={submitting}
          onClick={onConfirm}
        >
          {submitting ? 'Sending…' : 'Return my paper blank'}
        </button>
      </div>
    </section>
  );
}
